import React, { useState, useEffect } from "react";
import { feedApi } from "../../../services/api/feedApi";
import {
  FiFileText,
  FiExternalLink,
  FiCalendar,
  FiBook,
  FiLink,
  FiChevronDown,
  FiChevronUp,
  FiUsers,
} from "react-icons/fi";
import "./ResearchPost.css";

const ResearchPost = ({ post, onUpdate }) => {
  const paper = post.researchPaper || {};
  const [showFullAbstract, setShowFullAbstract] = useState(false);

  // Auto-refresh PDF URL after 9 minutes (they expire after 10)
  useEffect(() => {
    if (paper.pdfUrl) {
      const timer = setTimeout(async () => {
        try {
          const response = await feedApi.getPost(post._id);
          if (onUpdate && response.data?.researchPaper) {
            onUpdate(post._id, { researchPaper: response.data.researchPaper });
          }
        } catch (error) {
          // Silent fail for background refresh
        }
      }, 9 * 60 * 1000);

      return () => clearTimeout(timer);
    }
  }, [post._id, paper.pdfUrl, onUpdate]);

  const abstractText = paper.abstract || "";
  const isLongAbstract = abstractText.length > 250;

  return (
    <div className="cbx-rp">
      {/* Paper Card */}
      <div className="cbx-rp__card">
        {/* Top accent bar */}
        <div className="cbx-rp__accent" />

        {/* Paper type badge */}
        <div className="cbx-rp__badge">
          <FiFileText className="cbx-rp__badge-icon" />
          <span>Research Paper</span>
        </div>

        {/* Title */}
        <h3 className="cbx-rp__title">
          {paper.title || "Research Paper"}
        </h3>

        {/* Authors */}
        {paper.authors && paper.authors.length > 0 && (
          <div className="cbx-rp__authors">
            <FiUsers className="cbx-rp__authors-icon" />
            <span>{paper.authors.join(", ")}</span>
          </div>
        )}

        {/* Meta row */}
        <div className="cbx-rp__meta">
          {paper.journal && (
            <div className="cbx-rp__meta-chip">
              <FiBook />
              <span>{paper.journal}</span>
            </div>
          )}
          {paper.publicationDate && (
            <div className="cbx-rp__meta-chip">
              <FiCalendar />
              <span>{new Date(paper.publicationDate).getFullYear()}</span>
            </div>
          )}
          {paper.doi && (
            <div className="cbx-rp__meta-chip cbx-rp__meta-chip--doi">
              <FiLink />
              <span>DOI: {paper.doi}</span>
            </div>
          )}
        </div>

        {/* Abstract */}
        {abstractText && (
          <div className="cbx-rp__abstract-section">
            <h4 className="cbx-rp__abstract-label">Abstract</h4>
            <p className={`cbx-rp__abstract ${!showFullAbstract && isLongAbstract ? "cbx-rp__abstract--clamped" : ""}`}>
              {abstractText}
            </p>
            {isLongAbstract && (
              <button
                className="cbx-rp__abstract-toggle"
                onClick={() => setShowFullAbstract(!showFullAbstract)}
              >
                {showFullAbstract ? (
                  <>Show less <FiChevronUp /></>
                ) : (
                  <>Read more <FiChevronDown /></>
                )}
              </button>
            )}
          </div>
        )}

        {/* PDF Link */}
        {paper.pdfUrl && (
          <a
            href={paper.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="cbx-rp__pdf-btn"
          >
            <FiFileText />
            <span>View Full Paper</span>
            <FiExternalLink className="cbx-rp__pdf-btn-ext" />
          </a>
        )}
      </div>

      {/* Additional Content */}
      {post.content && (
        <p className="cbx-rp__content">{post.content}</p>
      )}

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="cbx-rp__tags">
          {post.tags.map((tag, index) => (
            <span key={index} className="cbx-rp__tag">
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResearchPost;
